import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { User, CreditCard, Activity, RefreshCw } from 'lucide-react';
import { useBatmanAuth } from '../context/BatmanAuthContext';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';

const Dashboard = () => {
  const { user, fetchAccount } = useBatmanAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchAccount();
    setIsRefreshing(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <div className="animate-pulse text-white/50">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      <main className="pt-24 pb-20">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">DASHBOARD</span>
                <h1 className="text-4xl md:text-5xl font-light text-white tracking-tight">Welcome back, {user.username}</h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white/70 hover:text-white hover:bg-white/5 transition-colors text-sm"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black font-medium hover:bg-white/90 transition-colors text-sm"
                >
                  <CreditCard className="h-4 w-4" />
                  Buy Credits
                </Link>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-px bg-white/10 mb-16">
              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">ACCOUNT</span>
                  <User className="h-4 w-4 text-white/40" />
                </div>
                <div className="text-3xl font-light text-white mb-2">{user.username}</div>
                <span className="text-white/50 text-sm">Account overview</span>
              </div>

              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">AVAILABLE CREDITS</span>
                  <CreditCard className="h-4 w-4 text-white/40" />
                </div>
                <div className="text-3xl font-light text-white mb-2">{user.codingCredits?.toLocaleString() || 0}</div>
                <span className="text-white/50 text-sm">Current usable balance</span>
              </div>

              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">TOTAL CREDITS</span>
                  <CreditCard className="h-4 w-4 text-blue-400/50" />
                </div>
                <div className="text-3xl font-light text-blue-400 mb-2">{((user.codingCredits || 0) + (user.mcqCredits || 0)).toLocaleString()}</div>
                <span className="text-white/50 text-sm">Combined account balance</span>
              </div>

              <div className="bg-[#111111] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-white/40 text-xs font-mono tracking-widest">DAILY USAGE</span>
                  <Activity className="h-4 w-4 text-white/40" />
                </div>
                <div className="text-3xl font-light text-white mb-2">
                  {user.dailyRequestsUsed || 0} / {user.dailyLimit || 100}
                </div>
                <div className="mt-4 w-full bg-white/10 h-1">
                  <div
                    className="bg-white h-1 transition-all"
                    style={{ width: `${Math.min(((user.dailyRequestsUsed || 0) / (user.dailyLimit || 100)) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
